<template>
  <div class="url-data-cell">
    <span
      class="material-icons-round logo"
      :class="{ successful: !isVulnerable }"
      :style="`color: ${vulnerabilityIcon.color}`"
      v-if='!isVulnerable'
    >
        {{ vulnerabilityIcon.iconName }}
      </span>
    <custom-tooltip popper-class="light-tooltip" v-else>
      <div slot='buttonContent'>
          <span class="material-icons-round logo" :style="`color: ${vulnerabilityIcon.color}`"  @click='goToIssues'>
            {{ vulnerabilityIcon.iconName }}
          </span>
      </div>
      <div slot="content">
        <slot name="tooltip">
          {{ warningTooltip }}
        </slot>
      </div>
    </custom-tooltip>
    <div class="url-data-cell__data">
      <custom-tooltip class="url-data-cell__data__tooltip" placement="right" popper-class="light-tooltip">
        <div slot='buttonContent'>
          {{ formatUrl }}
        </div>
        <div slot="content">
          <slot name="tooltip">
            {{ formatUrl }}
          </slot>
        </div>
      </custom-tooltip>
    </div>
    <div class='url-data-cell__logo'>
      <img  v-if="logo" :src="logo" />
    </div>
    <div class="url-data-cell__open">
      <custom-tooltip placement="right" popper-class="light-tooltip">
        <div slot='buttonContent'>
          <v-btn  class="open-cloud-btn" :href="formatUrl" target="_blank" :ripple="false" @click.stop="">
            <v-icon>open_in_new</v-icon>
          </v-btn>
        </div>
        <div slot="content">
          <slot name="tooltip">
            Open URI
          </slot>
        </div>
      </custom-tooltip>
    </div>
  </div>
</template>

<script>
import azureIcon from '@/assets/azure.svg';
import awsIcon from '@/assets/aws.svg';
import gcpIcon from '@/assets/gcp.svg';
import awsAuthenticatedIcon from '@/assets/aws_authenticated_icon.svg';
import azureAuthenticatedIcon from '@/assets/azure_authenticated_icon.svg';
import gcpAuthenticatedIcon from '@/assets/gcp_authenticated_icon.svg';
import { CONFIGURATION } from '@/constants/configuration';
import { views } from '@/router/constants';
import CustomTooltip from '@/components/common/CustomTooltip.vue';

export default {
  name: 'UrlTableCell',
  props: {
    item: {
      type: Object,
      required: true,
    },
    column: {
      type: Object,
      required: true,
    },
  },
  components: {
    CustomTooltip,
  },
  methods: {
    async goToIssues() {
      await this.$router.push({ name: views.ISSUES, params: { tld: this.item.url, specificSearch: 'true' }});
    },
  },
  computed: {
    logo() {
      if (this.item.source) {
        if (this.item.source === CONFIGURATION.SOURCE_TYPE.aws) {
          if (this.item.authenticated) {
            return awsAuthenticatedIcon;
          }
          return awsIcon;
        }
        if (this.item.source === CONFIGURATION.SOURCE_TYPE.gcp) {
          if (this.item.authenticated) {
            return gcpAuthenticatedIcon;
          }
          return gcpIcon;
        }
        if (this.item.source === CONFIGURATION.SOURCE_TYPE.azure) {
          if (this.item.authenticated) {
            return azureAuthenticatedIcon;
          }
          return azureIcon;
        }
      }
      return null;
    },
    warningTooltip() {
      return this.$t('configuration.vulnerable.asset.tooltip');
    },
    getData() {
      return this.item.url ? this.item.url.replaceAll('__', '.') : this.item.cloud.replaceAll('__', '.');
    },
    isVulnerable() {
      return Object.keys(this.item.vulnerabilityDetails).some(key => this.item.vulnerabilityDetails[key] > 0);
    },
    vulnerabilityIcon() {
      const vulnerabilityKeys = Object.keys(this.item.vulnerabilityDetails);
      if ((vulnerabilityKeys.includes('critical') && this.item.vulnerabilityDetails.critical > 0) || (vulnerabilityKeys.includes('high') && this.item.vulnerabilityDetails.high > 0)) {
        return { iconName: 'warning', color: '#E0255E' };
      }
      if (vulnerabilityKeys.includes('medium') && this.item.vulnerabilityDetails.medium > 0) {
        return { iconName: 'warning', color: '#e0ad42' };
      }
      if (vulnerabilityKeys.includes('low') && this.item.vulnerabilityDetails.low > 0) {
        return { iconName: 'warning', color: '#2459D5' };
      }
      return { iconName: 'check_circle', color: '#2baf69' };
    },
    formatUrl() {
      const url = this.getData;
      if (this.item.authenticated) {
        return url;
      }
      return url.startsWith('http') ? url : `http://${url}`;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/design-system';

.url-data-cell {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding-right: 50px;
  align-items: center;
  .material-icons-round {
    margin-right: 5px;
    &.successful {
      &:hover {
        cursor: auto !important;
      }
    }
  }
  &__open {
    height: 50px;
    .open-cloud-btn {
      background-color: var(--btn-action-bg);
      text-align: center;
      box-shadow: none;
      min-width: 28px;
      max-width: 28px;
      min-height: 28px;
      max-height: 28px;
      user-select: none;
      color: var(--btn-action-border-color) !important;
      padding: 0;
      .v-icon {
        font-size: $font-size-lg !important;
        color: var(--btn-action-border-color) !important;
      }

      &:hover {
        cursor: pointer;
        border: 1px solid var(--btn-action-border-color);
        background-color: var(--btn-action-hover-bg) !important;
      }

      &:active {
        border: 1px solid var(--btn-action-border-color);
        background-color: var(--btn-action-hover-bg) !important;
      }
      &:before {
        color: transparent;
      }
    }
  }
  &__data {
    width: 90%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    &__tooltip {
      color: var(--text-primary);
      background-color: transparent;
      &:hover {
        cursor: default;
        color: var(--text-primary);
      }
    }
  }
  &__logo {
    max-width: 28px;
    max-height: 37px;
    min-height: 37px;
    display: flex;
    align-items: center;
    margin-top: 9px;
    margin-right: $space-2-5;
    img {
      width: 28px;
    }
  }
}
</style>
